import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {pick, uniqBy, omitBy, isNil, isEmpty, isNull} from "lodash";
import { Helmet } from "react-helmet";
import { useHistory, useParams } from "react-router-dom";

// SHARED
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";

// ANTD
import { Row, Col, Steps } from "antd";
import { CloseCircleOutlined, ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";

// HOOKS
import useSession from "Hooks/session.hook";
import { useQuery, useItemsPerPage } from "Hooks/index";

//SERVICES
import { listBatches, listBoxes, listTrolley } from "Services/delivery.services";
import { listSlotsData } from "Services/delivery-settings.services";

// HELPERS
import { decode, encode, toaster, toNum } from "Helpers/index";
import { Sorting, SortingMode } from "Types/common.types";

import { Tabs, StackScreen, Button } from "@life-line-lab/components-react";

import normalize from "Utils/normalizer";

//SCSS
import "../parcel-tracking-view.scss";
import BoxesFullScreen from "Components/delivery-management/parcel-tracking-view/boxes-fullscreen";
import TrolleyFullScreen from "Components/delivery-management/parcel-tracking-view/trolley-fullscreen";
import {useRecoilState} from "recoil";
import {GlobalFilterAtom} from "Recoil/global_filter.atom";

// ====================================================================
//   Timer Settings
// ====================================================================
const TIMER = 5; // starting timer
const TIMER_COOLDOWN = TIMER - 1; // after TIMER_COOLDOWN, button can then only be pressed

const TROLLEY_STATUS = ["LOADING", "LOADED", "DISPATCHED"];

// ====================================================================
//   Dynamic SetInterval
// ====================================================================
// Pause the interval when api is being called (applicable for slow internet conditions)
const useInterval = (callback, delay) => {
  const savedCallback = useRef<any>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };

    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

// ====================================================================
//   Main Component
// ====================================================================
const DeliveryFullscreenPage = ({}) => {
  const { t } = useTranslation();
  const query = useQuery();
  const { org } = useSession();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [upcomingBatches, setUpcomingBatches] = useState<any>([]);
  const [racks, setRacks] = useState<any>([]);
  const [trolleys, setTrolleys] = useState<any>([]);
  const [boxesSlot, setBoxesSlot] = useState<any>([]);
  const [boxes, setBoxes] = useState<any>([]);

  const [timeBeforeRefresh, setTimeBeforeRefresh] = useState<number>(TIMER);
  const [disableRefresh, setDisableRefresh] = useState<boolean>(true);
  const [pauseRefresh, setPauseRefresh] = useState<boolean>(false);
  const [refreshError, setRefreshError] = useState<boolean>(false);

  // helps with redirecting back to the correct previous site
  const subTab = query.get("subTab") || "4";

  // helps with directing to the correct tab
  const redirectBack = query.get("redirect") || null;
  const typeOfItem = query.get("type") || "0"; //  0 is boxes, 1 is trolleys >> dictates which screen should appear in fullscreen
  const returnType = query.get("return") || null; // if there is a need for another tab to access, use this

  const [tabActiveKey, setTabActiveKey] = useState(typeOfItem);
  const [globalFilterState, ] = useRecoilState(GlobalFilterAtom);
  const [filterObj, setFilterObj] = useState<any>({
    companyOrgID : (globalFilterState?.selectedOrgIDs?.length && globalFilterState?.selectedOrgIDs?.length == 1)
      ? globalFilterState?.selectedOrgIDs[0]
      : org._id
  })

  useEffect(()=>{
    if(!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length){
      setFilterObj({...filterObj, companyOrgID : globalFilterState?.selectedOrgIDs?.length == 1
          ? globalFilterState?.selectedOrgIDs[0]
          : org._id})
    }
  },[globalFilterState])
  const handleTabClick = (key: string) => {
    setTabActiveKey(key);
  };

  const handleCloseStackScreen = () => {
    if (redirectBack == "dm_setup") {
      let path;

      switch (returnType) {
        case "racks":
          path = `/delivery-management/settings/assets?page=1`;
          break;
        default:
          path = `/delivery-management/settings/assets?tabKey=${parseInt(typeOfItem) + 1}&page=1`;
      }

      history.push(path);
    } else {
      history.push(
        `/delivery-management/parcel-tracking?tabKey=2&subTab=${
          parseInt(subTab) + parseInt(typeOfItem)
        }`,
      );
    }
  };

  //  Interval
  // =================================
  useEffect(() => {
    handleAPICallsByTabs();
    setTimeBeforeRefresh(TIMER); // reset timer
  }, [tabActiveKey]);

  // 1000 to emulate a second
  // if data is still being fetched, pause the timer, and resume when ready
  useInterval(
    () => {
      setTimeBeforeRefresh((prev) => prev - 1);
    },
    pauseRefresh ? null : 1000,
  );

  useEffect(() => {
    // error handling
    if (refreshError) setPauseRefresh(true);
    else setPauseRefresh(false);

    // prevent abuse of api calling
    if (timeBeforeRefresh == TIMER_COOLDOWN) {
      setDisableRefresh(false);
    }

    // call api every TIMER seconds
    if (timeBeforeRefresh <= 0) {
      setDisableRefresh(true);
      setTimeBeforeRefresh(TIMER); // reset timer
      handleAPICallsByTabs();
    }
  }, [timeBeforeRefresh]);

  //  Other functions
  // =================================

  const fetchUpcomingBatches = async () => {
    setPauseRefresh && setPauseRefresh(true);

    let filters = omitBy({
      ...filters
    },isNil);
    const responseData = await listBatches(
      omitBy(filters, isNil),
      { offset: 0, limit: 25 },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setUpcomingBatches(responseData);
      setRefreshError && setRefreshError(false);
    } else {
      setRefreshError && setRefreshError(true);
    }

    setPauseRefresh && setPauseRefresh(false);
  };

  const fetchBoxes = async (status?: string) => {
    setPauseRefresh && setPauseRefresh(true);

    const filter = omitBy({
      ...filterObj
    },isNil);
    let filters: any = {};

    if (!isEmpty(status)) {
      filters = { ...filter, status: [status] };
    }

    const responseData = await listBoxes(
      omitBy(filters, isNil),
      { offset: 0, limit: 25 },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setBoxes(responseData);
      setRefreshError && setRefreshError(false);
    } else {
      setRefreshError && setRefreshError(true);
    }

    setPauseRefresh && setPauseRefresh(false);
  };

  const fetchSlotsData = async () => {
    setPauseRefresh && setPauseRefresh(true);
    const responseData = await listSlotsData(omitBy({
      ...filterObj
    },isNil));
    if (responseData && !responseData.error) {
      setBoxesSlot(responseData);
      setRefreshError && setRefreshError(false);
      // console.log('normalised',normalize.response.slots_data(responseData))
    } else {
      setRefreshError && setRefreshError(true);
    }

    setPauseRefresh && setPauseRefresh(false);
  };

  const fetchTrolleys = async () => {
    setPauseRefresh && setPauseRefresh(true);

    let filters = omitBy({ status: TROLLEY_STATUS,
      ...filterObj
    },isNil);
    const responseData = await listTrolley(
      omitBy(filters, isNil),
      { offset: 0, limit: 25 },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setTrolleys(responseData);
      setRefreshError && setRefreshError(false);
    } else {
      toaster("error", responseData.error);
      setRefreshError && setRefreshError(true);
    }

    setPauseRefresh && setPauseRefresh(false);
  };

  const handleAPICallsByTabs = () => {
    switch (tabActiveKey) {
      case "0":
        // fetchBoxes()
        fetchSlotsData();
        history.push(
          `/delivery-management/parcel-tracking/fullscreen?${
            redirectBack ? "redirect=dm_setup&" : ""
          }type=0${returnType ? `&return=${returnType}` : ""}`,
        );
        break;
      case "1":
        fetchTrolleys();
        fetchBoxes("FILLED");
        fetchUpcomingBatches();
        history.push(
          `/delivery-management/parcel-tracking/fullscreen?${
            redirectBack ? "redirect=dm_setup&" : ""
          }type=1`,
        );
        break;
      default:
    }
  };

  const RestartTimer = () => {
    return (
      <Button
        isDisabled={disableRefresh}
        label={t("action.refresh")}
        onClick={() => {
          setTimeBeforeRefresh(TIMER);
          handleAPICallsByTabs();
          setDisableRefresh(true);
        }}
        className="refresh-btn"
      />
    );
  };

  const tabs = [
    {
      tabKey: "0",
      tabName: "boxes",
      label: t("tab.boxes"),
      component: <BoxesFullScreen boxesObj={boxesSlot} />,
    },
    {
      tabKey: "1",
      tabName: "trolley",
      label: t("tab.trolley"),
      component: (
        <TrolleyFullScreen
          boxesToLoad={boxes || []}
          upcomingBatches={upcomingBatches || []}
          trolleyArray={trolleys || []}
        />
      ),
    },
  ];

  return (
    <div>
      <StackScreen
        title=""
        isOpen={true}
        renderHeader={<div></div>}
        onClose={handleCloseStackScreen}
      >
        <div className="p-20 parcel-tracking-view">
          <div className="fullscreen-view">
            <div className="timer-div">
              {refreshError ? (
                <div className="dm-info dm-error">
                  <ExclamationCircleOutlined style={{ fontSize: "16px " }} />
                  <div>{t("error.updatingInfo")}</div>
                </div>
              ) : (
                <>
                  <div className="timer-text">
                    {t("label.refreshingIn")}:{" "}
                    {pauseRefresh ? <LoadingOutlined /> : timeBeforeRefresh}
                  </div>
                  <div className="timer-btn">
                    <RestartTimer />
                  </div>
                </>
              )}
            </div>
            <Tabs
              destroyInactiveTabPane={true}
              centered
              onTabClick={handleTabClick}
              defaultActiveKey="0"
              activeKey={tabActiveKey}
              tabBarGutter={25}
              tabs={tabs}
            />
          </div>
        </div>
      </StackScreen>
    </div>
  );
};

export default DeliveryFullscreenPage;
